import React, { Component } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import Cookies from 'universal-cookie';
import i18n from 'i18next';
import { getAwsApiBaseUrl } from '../../utils/awsApiBaseUrl';
import { userActions } from '../../actions';
import { storeLUCCredentials } from '../../services/user.service';
import { unitService } from '../../services/unit.service';
import { removeLocalStorageItem } from '../../utils/cookieAndLocalStorageUtils';
import { redirectToLucLogin } from '../../utils/lucLogin';
import { secureGuid } from '../../utils/guid';
import { history } from '../../history';
import { userConstants } from '../../constants';
import { Liveuloading } from '../Liveuloading';
import GoSolo from '../../assets/images/go-solo.png';

const LOGIN_REQUEST_TIMEOUT_MS = 30000;
// Marker header from the original 2FA implementation. The login lambda
// authenticates from the LUC session cookie and does not read it; it stays
// until the lambda's contract is finalized.
const LOGIN_MARKER = 'cookie regeneratetoken';
const cookies = new Cookies();

// Mock Header to maintain design during AuthCallback without causing errors
const MockHeader = () => (
  <header>
    <div className='navbar d-flex flex-md-row flex-column align-items-end p-0'>
      <div className='logoMainCol'>
        <div className='navbar-header'>
          <img src={GoSolo} alt='GoSolo' className='logoMain' />
        </div>
      </div>
      <div className='navMainCol d-flex flex-column px-0'>
        <div
          className='d-flex flex-row align-items-md-end navsection'
          style={{ height: '54px' }}
        ></div>
      </div>
    </div>
  </header>
);

// Mock Footer to maintain design during AuthCallback
const MockFooter = () => (
  <footer
    className='d-flex px-2'
    style={{ minHeight: '40px', backgroundColor: '#000' }}
  >
    <div className='ml-auto'></div>
  </footer>
);

class AuthCallbackComponent extends Component {
  // error: null, 'failed' (could not complete login) or 'unsupported' (the
  // account type is not allowed). On error we stay on this page and let the
  // user choose to try again. Sending them to /login would redirect straight
  // back to LUC, which returns here, which fails again: an endless loop.
  state = { error: null };

  componentDidMount() {
    this.authenticateWithCookies();
  }

  tryAgain = () => {
    redirectToLucLogin();
  };

  async authenticateWithCookies() {
    const { dispatch } = this.props;

    const d = new Date();
    d.setTime(d.getTime() + 1 * 24 * 60 * 60 * 1000);

    try {
      const apiBaseUrl = getAwsApiBaseUrl();
      if (!apiBaseUrl) {
        throw new Error('REACT_APP_AWSServerURL is not set');
      }

      // Generate user_session BEFORE the API call so it's available as
      // x-user-uuid. It is part of the server-side session key, so it must be
      // unguessable.
      cookies.set('user_session', secureGuid(), {
        path: '/',
        expires: d,
        secure: true,
      });

      const response = await axios.post(`${apiBaseUrl}/awsluc/login`, null, {
        headers: {
          Authorization: LOGIN_MARKER,
          'x-user-uuid': cookies.get('user_session'),
        },
        withCredentials: true,
        timeout: LOGIN_REQUEST_TIMEOUT_MS,
      });

      const credentials = response.data?.data?.credentials;
      if (!credentials?.access_token) {
        throw new Error('No access_token in login response');
      }

      if (credentials.user?.username) {
        cookies.set('username', credentials.user.username.toLowerCase(), {
          path: '/',
          expires: d,
          secure: true,
        });
      }

      // Mirror storeData: persist access_token + fetch inventory cookies
      await storeLUCCredentials(credentials.access_token);

      // Mark the login as successful and show the admin message
      dispatch({ type: userConstants.LOGIN_SUCCESS });
      cookies.set('admin_message_visible', true, {
        path: '/',
        secure: true,
      });

      // Fetch and dispatch the user info, then reject unsupported account types
      const userInfo = await dispatch(userActions.getUserInfo());
      if (userInfo?.type && userInfo.type !== 'luc_application') {
        // End the LUC session too, so "try again" shows the LUC login form
        // instead of silently signing this same account back in.
        try {
          await unitService.clearAccessToken();
        } catch (e) {
          console.error('[AuthCallback] Could not end the LUC session');
        }
        removeLocalStorageItem('access_token');
        dispatch({ type: userConstants.LOGIN_FAILURE });
        this.setState({ error: 'unsupported' });
        return;
      }

      if (!cookies.get('username')) {
        const resolvedUsername = userInfo?.email;
        if (resolvedUsername) {
          cookies.set('username', String(resolvedUsername).toLowerCase(), {
            path: '/',
            expires: d,
            secure: true,
          });
        }
      }

      history.push('/dashboard/units');
    } catch (error) {
      console.error('[AuthCallback] Login failed:', error?.message || error);
      removeLocalStorageItem('access_token');
      this.setState({ error: 'failed' });
    }
  }

  renderError() {
    const message =
      this.state.error === 'unsupported'
        ? i18n.t('CONTACTSUPPORT')
        : i18n.t('SESSIONEXPIRED');
    return (
      <div className='text-center py-5' role='alert'>
        <h4>{i18n.t('ERROR')}</h4>
        <p className='my-3'>{message}</p>
        <button
          type='button'
          className='btn btn-primary btn-sm'
          onClick={this.tryAgain}
        >
          {i18n.t('TRYAGAIN')}
        </button>
      </div>
    );
  }

  render() {
    return (
      <div
        id='wrapper'
        className='liveu_page'
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          margin: '0 auto',
        }}
      >
        <MockHeader />
        <div id='page-wrapper' className='mainContentArea' style={{ flex: 1 }}>
          {this.state.error ? this.renderError() : <Liveuloading />}
        </div>
        <MockFooter />
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({ dispatch });

export const AuthCallback = connect(
  null,
  mapDispatchToProps,
)(AuthCallbackComponent);
